/**
 * FGX-106 — Mobile-only dynamic skeleton controller.
 *
 * Exposed as `window.MobileSkeleton`. Responsibilities:
 *   1. Initial-mount hand-off. Visibility for `data-skeleton-initial` hosts is
 *      handled by inlined critical CSS in <head> (no dependency on this file).
 *      This controller removes `data-skeleton-initial` and sets
 *      `aria-busy="false"` after the first paint. A head inline script runs a
 *      4s safety release if this bundle is late.
 *   2. Async show / hide API used by `fg-facets.js` and others. `show()`
 *      defers setting `aria-busy="true"` by ~200ms to avoid skeleton flash
 *      on near-instant cache hits or aborted requests.
 *   3. Stale-response protection. `abortPrevious(host)` returns a fresh
 *      `AbortController` and cancels the previous one bound to the same
 *      host, so concurrent filter clicks don't race.
 *   4. Column-count sync. PLP / search grid placeholders read the
 *      `plp-grid-btn` cookie (set by `fg-save-grid-plp-mobile.js`) to render
 *      1- or 2-column layouts that match the user's saved preference.
 *
 * The controller is a no-op above the Tailwind `lmd` breakpoint (1024px);
 * the CSS already enforces this, but we also short-circuit work in JS so
 * desktop never pays.
 */
(function () {
  if (window.MobileSkeleton) return;

  /** Tailwind `lmd` breakpoint. Below this we run; at or above we no-op. */
  var MOBILE_MQ = window.matchMedia('(max-width: 1023.98px)');

  /** Delay before setting `aria-busy="true"` on async paths (anti-flash). */
  var ASYNC_SHOW_DELAY_MS = 200;

  /** Safety: never hold a skeleton longer than this even if nobody hides it. */
  var SAFETY_TIMEOUT_MS = 4000;

  /**
   * Pending `setTimeout` handles per host (used by `show()`).
   * @type {WeakMap<HTMLElement, number>}
   */
  var showTimers = new WeakMap();

  /**
   * Active `AbortController` per host so `abortPrevious()` can cancel
   * superseded fetches.
   * @type {WeakMap<HTMLElement, AbortController>}
   */
  var pendingControllers = new WeakMap();

  /**
   * @returns {boolean} `true` when the viewport is below the desktop cutoff.
   */
  function isMobile() {
    return MOBILE_MQ.matches;
  }

  /**
   * Resolve a selector or element to an HTMLElement.
   * @param {string|HTMLElement|null|undefined} target
   * @returns {HTMLElement|null}
   */
  function resolve(target) {
    if (!target) return null;
    return typeof target === 'string' ? document.querySelector(target) : target;
  }

  /**
   * Read a cookie value by name.
   * @param {string} name
   * @returns {string|null}
   */
  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Resolve the mobile column count for grid skeletons.
   * @returns {1|2}
   */
  function getColumns() {
    return readCookie('plp-grid-btn') === 'grid-mobile' ? 2 : 1;
  }

  /**
   * Push the current column count into every grid placeholder inside `host`.
   * @param {HTMLElement} host
   */
  function applyColumns(host) {
    var cols = String(getColumns());
    var grids = host.querySelectorAll('[data-skeleton-grid]');
    for (var i = 0; i < grids.length; i++) {
      grids[i].setAttribute('data-cols', cols);
    }
  }

  /**
   * Detect whether the host's real content is an empty-state UI. Scoped to
   * `[data-section-content]` so unrelated empty markers elsewhere on the page
   * do not block this host's initial hand-off.
   * @param {HTMLElement} host
   * @returns {boolean}
   */
  function hasEmptyState(host) {
    if (!host) return false;
    var content = host.querySelector('[data-section-content]');
    if (!content) return false;
    if (content.matches('.collection--empty, .template-search--empty')) return true;
    return !!content.querySelector('.collection--empty, .template-search--empty');
  }

  /**
   * Set the `aria-busy` flag on the host.
   * @param {HTMLElement} host
   * @param {boolean} busy
   */
  function setBusy(host, busy) {
    if (host) host.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * Schedule the skeleton on the host. Idempotent. No-op on desktop or when
   * the host is showing an empty state.
   * @param {string|HTMLElement} target
   */
  function show(target) {
    var host = resolve(target);
    if (!host) return;
    if (!isMobile()) { setBusy(host, false); return; }
    // Initial SSR hosts hand off on their own schedule; async show must not
    // re-busy them before that paint completes.
    if (host.hasAttribute('data-skeleton-initial')) return;

    var existing = showTimers.get(host);
    if (existing) clearTimeout(existing);

    var t = setTimeout(function () {
      if (!isMobile() || hasEmptyState(host)) { setBusy(host, false); return; }
      if (host.dataset.skeletonLayout === 'grid') applyColumns(host);
      setBusy(host, true);
    }, ASYNC_SHOW_DELAY_MS);
    showTimers.set(host, t);
  }

  /**
   * Hide the skeleton on the host. Safe to call multiple times; also clears
   * any pending `show()` timer so a previously-scheduled show doesn't fire
   * after the content has already loaded.
   * @param {string|HTMLElement} target
   */
  function hide(target) {
    var host = resolve(target);
    if (!host) return;
    var existing = showTimers.get(host);
    if (existing) { clearTimeout(existing); showTimers.delete(host); }
    setBusy(host, false);
  }

  /**
   * Abort the previous in-flight controller bound to `host` (if any) and
   * return a fresh one. Callers should pass the returned controller's
   * `signal` to `fetch()` and call `clearController()` once they're done.
   * @param {string|HTMLElement} target
   * @returns {AbortController|null}
   */
  function abortPrevious(target) {
    var host = resolve(target);
    if (!host) return null;
    var prev = pendingControllers.get(host);
    if (prev) {
      try { prev.abort(); } catch (e) { /* no-op */ }
    }
    var ctrl = new AbortController();
    pendingControllers.set(host, ctrl);
    return ctrl;
  }

  /**
   * Forget `ctrl` so future `abortPrevious()` calls don't abort it. Used by
   * the resolving callback so the controller is GCable.
   * @param {string|HTMLElement} target
   * @param {AbortController|null} ctrl
   */
  function clearController(target, ctrl) {
    var host = resolve(target);
    if (!host || !ctrl) return;
    if (pendingControllers.get(host) === ctrl) pendingControllers.delete(host);
  }

  /**
   * Complete the initial-mount hand-off for a single host. Clears
   * `data-skeleton-initial` so async `show()` can run on the same host later.
   * @param {HTMLElement} host
   */
  function finishInitialHost(host) {
    if (!host) return;
    host.removeAttribute('data-skeleton-initial');
    setBusy(host, false);
  }

  /**
   * Reveal one SSR / initial-mount host after its own skeleton frame. Each
   * host schedules its own animation frames — no shared gate across hosts.
   * @param {HTMLElement} host
   */
  function revealInitialHost(host) {
    if (!host || !host.hasAttribute('data-skeleton-initial')) return;

    if (!isMobile()) {
      finishInitialHost(host);
      return;
    }

    if (hasEmptyState(host)) {
      finishInitialHost(host);
      return;
    }

    if (host.dataset.skeletonLayout === 'grid') applyColumns(host);

    var safety = setTimeout(function () {
      finishInitialHost(host);
    }, SAFETY_TIMEOUT_MS);

    // Force style/layout while busy so this host's skeleton paints before swap.
    void host.offsetHeight;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        clearTimeout(safety);
        finishInitialHost(host);
      });
    });
  }

  /**
   * Hand off every current initial-mount host. Safe to call more than once;
   * hosts drop `data-skeleton-initial` as they reveal.
   */
  function initInitial() {
    var nodes = document.querySelectorAll('[data-skeleton-key][data-skeleton-initial]');
    for (var i = 0; i < nodes.length; i++) {
      revealInitialHost(nodes[i]);
    }
    if (typeof window.__fgSkeletonCancelSafety === 'function') {
      window.__fgSkeletonCancelSafety();
    }
  }

  /**
   * Watch for initial hosts inserted after the first boot (e.g. Section
   * Rendering API) and reveal them independently.
   */
  function observeInitialHosts() {
    if (typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];

        if (mutation.type === 'attributes' && mutation.attributeName === 'data-skeleton-initial') {
          var attrHost = mutation.target;
          if (attrHost instanceof HTMLElement && attrHost.hasAttribute('data-skeleton-initial')) {
            revealInitialHost(attrHost);
          }
          continue;
        }

        if (mutation.type !== 'childList') continue;

        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches('[data-skeleton-key][data-skeleton-initial]')) {
            revealInitialHost(node);
            return;
          }
          var nested = node.querySelectorAll('[data-skeleton-key][data-skeleton-initial]');
          for (var j = 0; j < nested.length; j++) {
            revealInitialHost(nested[j]);
          }
        });
      }
    });

    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-skeleton-initial']
    });
  }

  /**
   * Boot initial hosts after DOM ready and one frame so mobile skeleton CSS
   * has applied before per-host hand-off begins.
   */
  function bootInitialSkeletons() {
    requestAnimationFrame(initInitial);
    observeInitialHosts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootInitialSkeletons, { once: true });
  } else {
    bootInitialSkeletons();
  }

  window.MobileSkeleton = {
    show: show,
    hide: hide,
    isMobile: isMobile,
    getColumns: getColumns,
    abortPrevious: abortPrevious,
    clearController: clearController,
    revealInitialHost: revealInitialHost,
    initInitial: initInitial
  };
})();
